/**
 * Bookworm Avatar Library
 *
 * Twelve hand-crafted SVG avatars — each features a friendly bookworm reading
 * a book, with unique colors and accessories. Pure SVG (no network), so they
 * stay crisp at any size and load instantly.
 *
 * Each preset:
 *   - id: matches the backend BOOKWORM_AVATARS catalog
 *   - color: primary worm body color
 *   - accent: complementary color (book/glasses/etc.)
 *   - render: a function returning <svg> JSX
 */

const FRAME = (children, bg) => (
  <svg
    viewBox="0 0 64 64"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    width="100%"
    height="100%"
    preserveAspectRatio="xMidYMid meet"
    style={{ display: 'block' }}
  >
    <defs>
      <linearGradient id="bg-grad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor={bg[0]} />
        <stop offset="100%" stopColor={bg[1]} />
      </linearGradient>
    </defs>
    <rect width="64" height="64" rx="32" fill="url(#bg-grad)" />
    {children}
  </svg>
);

// Re-usable bookworm body parts
const Worm = ({ color, eyeOffset = 0 }) => (
  <g>
    {/* worm segments */}
    <ellipse cx="22" cy="46" rx="10" ry="7" fill={color} />
    <ellipse cx="32" cy="42" rx="9" ry="7" fill={color} />
    <ellipse cx="42" cy="38" rx="9" ry="7" fill={color} opacity="0.95" />
    <ellipse cx="46" cy="30" rx="8" ry="7" fill={color} />
    {/* head */}
    <circle cx="46" cy="22" r="9" fill={color} />
    {/* eyes */}
    <circle cx={43 + eyeOffset} cy="20" r="1.6" fill="#1F2937" />
    <circle cx={49 + eyeOffset} cy="20" r="1.6" fill="#1F2937" />
    {/* mouth */}
    <path d="M 44 25 Q 46 27 48 25" stroke="#1F2937" strokeWidth="1.2" fill="none" strokeLinecap="round" />
  </g>
);

const Book = ({ color = '#FFFFFF', spine = '#1F2937', x = 6, y = 32 }) => (
  <g transform={`translate(${x}, ${y})`}>
    <rect width="18" height="14" rx="1.5" fill={color} stroke={spine} strokeWidth="1" />
    <line x1="9" y1="0" x2="9" y2="14" stroke={spine} strokeWidth="0.8" />
    <line x1="3" y1="4" x2="7" y2="4" stroke="#9CA3AF" strokeWidth="0.6" />
    <line x1="3" y1="7" x2="7" y2="7" stroke="#9CA3AF" strokeWidth="0.6" />
    <line x1="11" y1="4" x2="15" y2="4" stroke="#9CA3AF" strokeWidth="0.6" />
    <line x1="11" y1="7" x2="15" y2="7" stroke="#9CA3AF" strokeWidth="0.6" />
  </g>
);

const Glasses = () => (
  <g>
    <circle cx="43" cy="20" r="3" fill="none" stroke="#1F2937" strokeWidth="1.4" />
    <circle cx="49" cy="20" r="3" fill="none" stroke="#1F2937" strokeWidth="1.4" />
    <line x1="46" y1="20" x2="46" y2="20" stroke="#1F2937" strokeWidth="1.4" />
  </g>
);

export const BOOKWORM_AVATARS = {
  wormy: {
    id: 'wormy',
    label: 'Wormy the Reader',
    color: '#7CC4A1',
    accent: '#FFD86E',
    render: () => FRAME(
      <>
        <Worm color="#7CC4A1" />
        <Book color="#FFD86E" spine="#7C3AED" />
      </>,
      ['#1E3A5F', '#0F172A']
    ),
  },
  specs: {
    id: 'specs',
    label: 'Specs the Scholar',
    color: '#F6A26B',
    accent: '#3D2C8D',
    render: () => FRAME(
      <>
        <Worm color="#F6A26B" />
        <Book color="#3D2C8D" spine="#FBBF24" />
        <Glasses />
      </>,
      ['#3D2C8D', '#1E1B4B']
    ),
  },
  latte: {
    id: 'latte',
    label: 'Latte the Cozy',
    color: '#C49A6C',
    accent: '#FBE7C6',
    render: () => FRAME(
      <>
        <Worm color="#C49A6C" />
        <Book color="#FBE7C6" spine="#7C2D12" />
        {/* steaming cup */}
        <g transform="translate(8, 12)">
          <rect x="0" y="6" width="8" height="8" rx="1" fill="#FFFFFF" stroke="#7C2D12" />
          <path d="M 8 9 Q 11 9 11 12 Q 11 14 8 13" fill="none" stroke="#7C2D12" strokeWidth="0.8" />
          <path d="M 2 4 Q 3 2 4 4" stroke="#FBE7C6" strokeWidth="0.8" fill="none" />
          <path d="M 5 4 Q 6 2 7 4" stroke="#FBE7C6" strokeWidth="0.8" fill="none" />
        </g>
      </>,
      ['#78350F', '#451A03']
    ),
  },
  midnight: {
    id: 'midnight',
    label: 'Midnight the Mystery',
    color: '#5D5FEF',
    accent: '#F3F0FF',
    render: () => FRAME(
      <>
        {/* moon */}
        <circle cx="14" cy="14" r="5" fill="#F3F0FF" opacity="0.9" />
        <circle cx="16" cy="13" r="4" fill="#1E1B4B" />
        {/* stars */}
        <circle cx="56" cy="10" r="0.8" fill="#F3F0FF" />
        <circle cx="50" cy="6" r="0.6" fill="#F3F0FF" />
        <circle cx="58" cy="18" r="0.6" fill="#F3F0FF" />
        <Worm color="#5D5FEF" />
        <Book color="#1E1B4B" spine="#A78BFA" />
      </>,
      ['#312E81', '#0F172A']
    ),
  },
  bloom: {
    id: 'bloom',
    label: 'Bloom the Romantic',
    color: '#F472B6',
    accent: '#FFE5EC',
    render: () => FRAME(
      <>
        <Worm color="#F472B6" />
        <Book color="#FFE5EC" spine="#9D174D" />
        {/* heart over book */}
        <path d="M 14 28 a 2 2 0 0 1 4 0 a 2 2 0 0 1 4 0 q 0 2 -4 5 q -4 -3 -4 -5 z" fill="#EF4444" />
      </>,
      ['#831843', '#4C0519']
    ),
  },
  quill: {
    id: 'quill',
    label: 'Quill the Poet',
    color: '#EAB308',
    accent: '#1F2937',
    render: () => FRAME(
      <>
        <Worm color="#EAB308" />
        <Book color="#FEF3C7" spine="#92400E" />
        {/* quill */}
        <g transform="translate(48, 6) rotate(20)">
          <line x1="0" y1="0" x2="6" y2="14" stroke="#1F2937" strokeWidth="1.2" />
          <path d="M 0 0 Q -2 4 0 6 Q 2 4 0 0" fill="#FBBF24" />
        </g>
      </>,
      ['#7C2D12', '#1F2937']
    ),
  },
  crumb: {
    id: 'crumb',
    label: 'Crumb the Snacker',
    color: '#F59E0B',
    accent: '#7C2D12',
    render: () => FRAME(
      <>
        <Worm color="#F59E0B" />
        <Book color="#FFEDD5" spine="#7C2D12" />
        {/* cookie */}
        <g transform="translate(46, 50)">
          <circle r="6" fill="#A16207" />
          <circle cx="-2" cy="-1" r="0.8" fill="#451A03" />
          <circle cx="2" cy="1" r="0.8" fill="#451A03" />
          <circle cx="0" cy="2" r="0.8" fill="#451A03" />
          <circle cx="2" cy="-2" r="0.6" fill="#451A03" />
        </g>
      </>,
      ['#92400E', '#451A03']
    ),
  },
  luna: {
    id: 'luna',
    label: 'Luna the Dreamer',
    color: '#A78BFA',
    accent: '#FDE68A',
    render: () => FRAME(
      <>
        {/* sparkles */}
        <path d="M 12 12 l 1 -3 l 1 3 l 3 1 l -3 1 l -1 3 l -1 -3 l -3 -1 z" fill="#FDE68A" />
        <path d="M 56 14 l 0.6 -2 l 0.6 2 l 2 0.6 l -2 0.6 l -0.6 2 l -0.6 -2 l -2 -0.6 z" fill="#FDE68A" />
        <Worm color="#A78BFA" />
        <Book color="#EDE9FE" spine="#5B21B6" />
      </>,
      ['#4C1D95', '#1E1B4B']
    ),
  },
  pip: {
    id: 'pip',
    label: 'Pip the Explorer',
    color: '#10B981',
    accent: '#064E3B',
    render: () => FRAME(
      <>
        {/* compass */}
        <g transform="translate(12, 14)">
          <circle r="6" fill="#FEF3C7" stroke="#7C2D12" strokeWidth="1" />
          <path d="M 0 -4 L 1 0 L 0 4 L -1 0 Z" fill="#EF4444" />
        </g>
        <Worm color="#10B981" />
        <Book color="#FEF3C7" spine="#064E3B" />
      </>,
      ['#064E3B', '#022C22']
    ),
  },
  scribble: {
    id: 'scribble',
    label: 'Scribble the Writer',
    color: '#06B6D4',
    accent: '#0E7490',
    render: () => FRAME(
      <>
        <Worm color="#06B6D4" />
        <Book color="#E0F7FA" spine="#0E7490" />
        {/* pencil */}
        <g transform="translate(7, 8) rotate(45)">
          <rect width="14" height="3" fill="#FBBF24" />
          <polygon points="14,0 17,1.5 14,3" fill="#1F2937" />
          <rect x="-2" width="2" height="3" fill="#EF4444" />
        </g>
      </>,
      ['#164E63', '#0F172A']
    ),
  },
  ember: {
    id: 'ember',
    label: 'Ember the Bold',
    color: '#EF4444',
    accent: '#FCA5A5',
    render: () => FRAME(
      <>
        <Worm color="#EF4444" />
        <Book color="#FEE2E2" spine="#7F1D1D" />
        {/* flame */}
        <g transform="translate(12, 8)">
          <path d="M 0 8 Q -2 4 0 0 Q 2 4 4 2 Q 5 6 3 9 Q 1 10 0 8 Z" fill="#F59E0B" />
          <path d="M 1 7 Q 0 5 1 3 Q 2 5 3 4 Q 3 7 2 8 Z" fill="#FCD34D" />
        </g>
      </>,
      ['#7F1D1D', '#1F2937']
    ),
  },
  sage: {
    id: 'sage',
    label: 'Sage the Wise',
    color: '#84CC16',
    accent: '#365314',
    render: () => FRAME(
      <>
        <Worm color="#84CC16" />
        <Book color="#ECFCCB" spine="#365314" />
        <Glasses />
        {/* leaf */}
        <g transform="translate(8, 8)">
          <path d="M 0 4 Q 4 -2 8 4 Q 4 6 0 4 Z" fill="#65A30D" />
          <line x1="0" y1="4" x2="8" y2="4" stroke="#365314" strokeWidth="0.6" />
        </g>
      </>,
      ['#365314', '#1A2E05']
    ),
  },

  // ===== DISNEY-INSPIRED BOOKWORMS =====
  // Each avatar nods to a beloved animation archetype using costume & color
  // language only — no copyrighted likenesses.
  mouseling: {
    id: 'mouseling',
    label: 'Mouseling the Cheerful',
    color: '#1F2937',
    accent: '#EF4444',
    render: () => FRAME(
      <>
        {/* big round mouse ears */}
        <circle cx="40" cy="10" r="6" fill="#1F2937" />
        <circle cx="52" cy="10" r="6" fill="#1F2937" />
        <Worm color="#1F2937" />
        {/* red shorts with white buttons */}
        <ellipse cx="22" cy="50" rx="11" ry="5" fill="#EF4444" />
        <circle cx="18" cy="49" r="1.2" fill="#FFFFFF" />
        <circle cx="26" cy="49" r="1.2" fill="#FFFFFF" />
        <Book color="#FACC15" spine="#7C2D12" x="4" y="34" />
      </>,
      ['#7F1D1D', '#1F2937']
    ),
  },
  princessa: {
    id: 'princessa',
    label: 'Princessa the Royal',
    color: '#FBCFE8',
    accent: '#FDE047',
    render: () => FRAME(
      <>
        <Worm color="#FBCFE8" />
        {/* sparkly tiara */}
        <g transform="translate(40, 8)">
          <path d="M 0 6 L 3 0 L 6 4 L 9 0 L 12 6 Z" fill="#FDE047" stroke="#92400E" strokeWidth="0.6" />
          <circle cx="3" cy="2" r="0.8" fill="#FB7185" />
          <circle cx="9" cy="2" r="0.8" fill="#60A5FA" />
          <circle cx="6" cy="4" r="0.8" fill="#A78BFA" />
        </g>
        <Book color="#FFFFFF" spine="#BE185D" />
      </>,
      ['#831843', '#3B0764']
    ),
  },
  frosty: {
    id: 'frosty',
    label: 'Frosty the Snowbound',
    color: '#7DD3FC',
    accent: '#E0F2FE',
    render: () => FRAME(
      <>
        {/* falling snowflakes */}
        <g fill="#FFFFFF">
          <circle cx="12" cy="10" r="1" />
          <circle cx="22" cy="6" r="0.8" />
          <circle cx="56" cy="14" r="0.8" />
          <circle cx="50" cy="6" r="1" />
        </g>
        <Worm color="#7DD3FC" />
        <Book color="#E0F2FE" spine="#0369A1" />
        {/* large snowflake on book */}
        <g transform="translate(15, 39)" stroke="#0369A1" strokeWidth="0.8" strokeLinecap="round">
          <line x1="0" y1="-3" x2="0" y2="3" />
          <line x1="-3" y1="0" x2="3" y2="0" />
          <line x1="-2" y1="-2" x2="2" y2="2" />
          <line x1="-2" y1="2" x2="2" y2="-2" />
        </g>
      </>,
      ['#082F49', '#0C4A6E']
    ),
  },
  wishful: {
    id: 'wishful',
    label: 'Wishful the Sorcerer',
    color: '#A78BFA',
    accent: '#FDE047',
    render: () => FRAME(
      <>
        {/* magical stars */}
        <path d="M 12 14 l 0.8 -2 l 0.8 2 l 2 0.8 l -2 0.8 l -0.8 2 l -0.8 -2 l -2 -0.8 z" fill="#FDE047" />
        <path d="M 56 8 l 0.6 -1.6 l 0.6 1.6 l 1.6 0.6 l -1.6 0.6 l -0.6 1.6 l -0.6 -1.6 l -1.6 -0.6 z" fill="#FDE047" />
        <Worm color="#A78BFA" />
        {/* pointy wizard hat with stars */}
        <g transform="translate(38, 4)">
          <path d="M 0 14 L 8 0 L 16 14 Z" fill="#1E3A8A" stroke="#FDE047" strokeWidth="0.6" />
          <circle cx="6" cy="10" r="0.7" fill="#FDE047" />
          <circle cx="10" cy="6" r="0.7" fill="#FDE047" />
          <rect x="-2" y="13" width="20" height="2.5" rx="0.5" fill="#1E3A8A" />
        </g>
        <Book color="#1E3A8A" spine="#FDE047" />
        {/* magic wand */}
        <g transform="translate(8, 28) rotate(-25)">
          <line x1="0" y1="0" x2="14" y2="0" stroke="#7C2D12" strokeWidth="1.2" />
          <path d="M -1 0 l -1.2 -1.2 l 1.2 -1.2 l 1.2 1.2 l 1.2 1.2 l -1.2 1.2 z" fill="#FDE047" />
        </g>
      </>,
      ['#312E81', '#1E1B4B']
    ),
  },
  roar: {
    id: 'roar',
    label: 'Roar the Brave',
    color: '#F59E0B',
    accent: '#92400E',
    render: () => FRAME(
      <>
        <Worm color="#F59E0B" />
        {/* lion mane around head */}
        <g fill="#92400E">
          <circle cx="46" cy="22" r="11.5" />
        </g>
        {/* spiky mane edges */}
        <g fill="#B45309">
          <polygon points="35,16 33,12 37,15" />
          <polygon points="56,16 60,12 56,15" />
          <polygon points="46,11 44,7 48,7" />
          <polygon points="35,30 31,32 36,30" />
          <polygon points="58,30 62,32 56,30" />
        </g>
        {/* re-draw face on top of mane */}
        <circle cx="46" cy="22" r="8" fill="#F59E0B" />
        <circle cx="43" cy="20" r="1.6" fill="#1F2937" />
        <circle cx="49" cy="20" r="1.6" fill="#1F2937" />
        <path d="M 44 25 Q 46 27 48 25" stroke="#1F2937" strokeWidth="1.2" fill="none" strokeLinecap="round" />
        <Book color="#FED7AA" spine="#92400E" />
      </>,
      ['#78350F', '#451A03']
    ),
  },
  beastly: {
    id: 'beastly',
    label: 'Beastly the Romantic',
    color: '#0EA5E9',
    accent: '#FBBF24',
    render: () => FRAME(
      <>
        <Worm color="#0EA5E9" />
        <Book color="#FCD34D" spine="#92400E" />
        {/* enchanted rose on book */}
        <g transform="translate(15, 38)">
          <line x1="0" y1="0" x2="0" y2="6" stroke="#16A34A" strokeWidth="0.8" />
          <path d="M -3 0 q 0 -3 3 -3 q 3 0 3 3 q 0 2 -3 3 q -3 -1 -3 -3 z" fill="#DC2626" />
          <circle cx="0" cy="0" r="0.8" fill="#FBBF24" />
        </g>
      </>,
      ['#1E3A8A', '#0F172A']
    ),
  },

  // ===== MARVEL-INSPIRED BOOKWORMS =====
  // Comic-book hero archetypes evoked through costume & accessory color motifs.
  spinner: {
    id: 'spinner',
    label: 'Spinner the Web-slinger',
    color: '#DC2626',
    accent: '#1E40AF',
    render: () => FRAME(
      <>
        {/* faint web pattern in background */}
        <g stroke="#1E40AF" strokeWidth="0.4" fill="none" opacity="0.6">
          <line x1="32" y1="0" x2="32" y2="64" />
          <line x1="0" y1="32" x2="64" y2="32" />
          <line x1="8" y1="8" x2="56" y2="56" />
          <line x1="56" y1="8" x2="8" y2="56" />
          <circle cx="32" cy="32" r="22" />
          <circle cx="32" cy="32" r="14" />
        </g>
        <Worm color="#DC2626" />
        {/* blue band around head */}
        <ellipse cx="46" cy="25" rx="9" ry="3" fill="#1E40AF" />
        {/* white spider eyes */}
        <ellipse cx="43" cy="20" rx="2.4" ry="1.6" fill="#FFFFFF" />
        <ellipse cx="49" cy="20" rx="2.4" ry="1.6" fill="#FFFFFF" />
        <Book color="#1E40AF" spine="#DC2626" />
      </>,
      ['#7F1D1D', '#0F172A']
    ),
  },
  mighty: {
    id: 'mighty',
    label: 'Mighty the Thunder',
    color: '#DC2626',
    accent: '#94A3B8',
    render: () => FRAME(
      <>
        {/* lightning bolt */}
        <path d="M 14 4 L 8 18 L 12 18 L 8 28 L 16 14 L 12 14 L 16 4 Z" fill="#FDE047" stroke="#92400E" strokeWidth="0.4" />
        <Worm color="#DC2626" />
        {/* winged silver helmet */}
        <g transform="translate(38, 8)">
          <ellipse cx="8" cy="6" rx="9" ry="5" fill="#94A3B8" stroke="#475569" strokeWidth="0.6" />
          <path d="M -2 6 q -3 -2 -1 -6 q 3 0 3 4 z" fill="#E5E7EB" stroke="#475569" strokeWidth="0.4" />
          <path d="M 18 6 q 3 -2 1 -6 q -3 0 -3 4 z" fill="#E5E7EB" stroke="#475569" strokeWidth="0.4" />
        </g>
        <Book color="#94A3B8" spine="#1F2937" />
        {/* hammer */}
        <g transform="translate(7, 38) rotate(15)">
          <rect x="-2" y="0" width="4" height="14" fill="#7C2D12" />
          <rect x="-6" y="-4" width="12" height="6" rx="0.5" fill="#94A3B8" stroke="#475569" strokeWidth="0.4" />
        </g>
      </>,
      ['#1E40AF', '#0F172A']
    ),
  },
  stark: {
    id: 'stark',
    label: 'Stark the Inventor',
    color: '#DC2626',
    accent: '#FDE047',
    render: () => FRAME(
      <>
        <Worm color="#DC2626" />
        {/* gold faceplate slits */}
        <rect x="40" y="19" width="3" height="1.6" fill="#FDE047" />
        <rect x="49" y="19" width="3" height="1.6" fill="#FDE047" />
        {/* arc-reactor on chest segment */}
        <circle cx="32" cy="42" r="3.6" fill="#FDE047" stroke="#FBBF24" strokeWidth="0.6" />
        <circle cx="32" cy="42" r="2.2" fill="#FFFFFF" />
        <circle cx="32" cy="42" r="1.2" fill="#7DD3FC" />
        <Book color="#FDE047" spine="#7F1D1D" />
      </>,
      ['#7F1D1D', '#1F2937']
    ),
  },
  shield: {
    id: 'shield',
    label: 'Shield the Captain',
    color: '#1E40AF',
    accent: '#DC2626',
    render: () => FRAME(
      <>
        <Worm color="#1E40AF" />
        {/* round shield: red/white/blue rings + star */}
        <g transform="translate(14, 36)">
          <circle r="9" fill="#DC2626" />
          <circle r="7" fill="#FFFFFF" />
          <circle r="5" fill="#DC2626" />
          <circle r="3" fill="#1E40AF" />
          <path d="M 0 -2.4 L 0.7 -0.8 L 2.3 -0.8 L 1 0.2 L 1.5 1.8 L 0 0.8 L -1.5 1.8 L -1 0.2 L -2.3 -0.8 L -0.7 -0.8 Z" fill="#FFFFFF" />
        </g>
        <Book color="#FFFFFF" spine="#DC2626" x="36" y="44" />
      </>,
      ['#1E3A8A', '#0F172A']
    ),
  },
  smash: {
    id: 'smash',
    label: 'Smash the Strong',
    color: '#16A34A',
    accent: '#7C3AED',
    render: () => FRAME(
      <>
        <Worm color="#16A34A" />
        {/* purple ripped shorts */}
        <g fill="#7C3AED">
          <polygon points="11,46 18,42 22,46 18,52 14,52 11,50" />
          <polygon points="22,46 28,40 32,46 28,52 24,52" />
        </g>
        <Book color="#86EFAC" spine="#14532D" />
        {/* impact lines */}
        <g stroke="#FDE047" strokeWidth="1" strokeLinecap="round">
          <line x1="6" y1="10" x2="10" y2="14" />
          <line x1="58" y1="10" x2="54" y2="14" />
          <line x1="58" y1="50" x2="54" y2="46" />
        </g>
      </>,
      ['#14532D', '#1F2937']
    ),
  },
  claws: {
    id: 'claws',
    label: 'Claws the Wild',
    color: '#FACC15',
    accent: '#1E40AF',
    render: () => FRAME(
      <>
        <Worm color="#FACC15" />
        {/* blue cowl with pointed mask */}
        <g fill="#1E40AF">
          <path d="M 36 16 Q 46 8 56 16 L 56 22 Q 51 26 46 26 Q 41 26 36 22 Z" />
          <polygon points="38,12 41,4 44,12" />
          <polygon points="48,12 51,4 54,12" />
        </g>
        {/* eyes through mask */}
        <ellipse cx="43" cy="20" rx="1.8" ry="1.2" fill="#FFFFFF" />
        <ellipse cx="49" cy="20" rx="1.8" ry="1.2" fill="#FFFFFF" />
        <Book color="#FEF3C7" spine="#1E40AF" />
        {/* three silver claws */}
        <g stroke="#E5E7EB" strokeWidth="1.4" strokeLinecap="round">
          <line x1="6" y1="32" x2="6" y2="22" />
          <line x1="10" y1="32" x2="10" y2="20" />
          <line x1="14" y1="32" x2="14" y2="22" />
        </g>
      </>,
      ['#1E3A8A', '#1F2937']
    ),
  },
};

export const AVATAR_LIST = Object.values(BOOKWORM_AVATARS);
export const DEFAULT_AVATAR_ID = 'wormy';

// Category mapping — used by the AvatarPicker to group presets into tabs.
// Any avatar id not listed here defaults to 'classic'.
export const AVATAR_CATEGORIES = {
  // Disney-inspired
  mouseling: 'disney',
  princessa: 'disney',
  frosty: 'disney',
  wishful: 'disney',
  roar: 'disney',
  beastly: 'disney',
  // Marvel-inspired
  spinner: 'marvel',
  mighty: 'marvel',
  stark: 'marvel',
  shield: 'marvel',
  smash: 'marvel',
  claws: 'marvel',
};

export const AVATAR_CATEGORY_LABELS = {
  classic: 'Classic',
  disney: 'Disney-inspired',
  marvel: 'Marvel-inspired',
};

export const getAvatarCategory = (id) => AVATAR_CATEGORIES[id] || 'classic';
